import { FlexBox, Icon, Text } from '@fable/components'
import { css, useTheme } from '@fable/theme'
import { useStats } from '../../lib/ctx/stats'
import { capitalize } from 'lodash'
import { analytics } from 'segment_analytics'
import { useReaderSummary } from '../../lib/ctx/reader_summary'

type SelectorType = 'goal' | 'reader-summary'

interface Props {
  selector: SelectorType
  onSelect: () => void
}

export const StatsItemOptInSelector = ({ selector, onSelect }: Props) => {
  const { colors } = useTheme()
  const [updateGoalOptIn, goalOptIn, year] = useStats((s) => [
    s.updateGoalOptIn,
    s.goalOptIn,
    s.year,
  ])
  const [summaryOptIn, updateSummaryOptIn] = useReaderSummary((s) => [
    s.summaryOptIn,
    s.updateSummaryOptIn,
  ])

  const getMenuItems = () => {
    switch (selector) {
      case 'goal':
        return [
          {
            label: 'Opt-in',
            value: true,
          },
          {
            label: 'Opt-out',
            value: false,
          },
        ]
      case 'reader-summary':
        return [
          {
            label: 'Yes',
            value: true,
          },
          {
            label: 'Not now',
            value: false,
          },
        ]
    }
  }

  const handleReaderSummaryOptIn = async (optIn: boolean) => {
    if (optIn === summaryOptIn) return

    onSelect()

    try {
      await updateSummaryOptIn(optIn)
    } catch (e) {
      console.error('Error toggling summary opt in', e)
    }
  }

  const handleGoalOptIn = async (optIn: boolean) => {
    if (optIn === goalOptIn) return

    onSelect()

    if (optIn) {
      analytics.events.stats.statsGoalShown({ year })
    } else {
      analytics.events.stats.statsGoalHidden({ year })
    }

    try {
      await updateGoalOptIn(optIn)
    } catch (e) {
      console.error('Error toggling goal opt in', e)
    }
  }

  const handleOptIn = (optIn: boolean) => {
    switch (selector) {
      case 'goal':
        handleGoalOptIn(optIn)
        break
      case 'reader-summary':
        handleReaderSummaryOptIn(optIn)
        break
    }
  }

  const getTitle = () => {
    switch (selector) {
      case 'goal':
        return goalOptIn ? 'Reading Goals' : 'Yearly Reading Goals'
      case 'reader-summary':
        return 'Join the Reader Summary beta program?'
    }
  }

  return (
    <div>
      <Text
        className={css`
          padding: 0 12px;
        `}
        type="label"
        sizing="L"
        color={colors.uiPewterGrey}
      >
        {getTitle()}
      </Text>
      <ul
        className={css`
          margin-top: 5px;
        `}
      >
        {getMenuItems().map((item, i) => {
          const value = item.value
          const getSelected = () => {
            switch (selector) {
              case 'goal':
                return value === goalOptIn
              case 'reader-summary':
                return value === summaryOptIn
            }
          }

          return (
            <li
              key={i}
              className={css`
                padding: 0 20px 0 12px;
                height: 32px;
                display: flex;
                align-items: center;
                cursor: pointer;
              `}
              onClick={() => handleOptIn(value)}
            >
              <FlexBox
                className={css`
                  width: 100%;
                `}
                as="span"
                alignItems="center"
                gap="8px"
              >
                <Text type="body" sizing="M" color={colors.uiWhite}>
                  {capitalize(item.label)}
                </Text>
              </FlexBox>
              <Icon
                className={css`
                  flex-shrink: 0;
                  opacity: ${getSelected() ? 1 : 0};
                `}
                type="tick"
                fill={colors.uiWhite}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}
